import { BUSINESS, SAME_AS, SITE_URL } from "@/lib/business";

/** Serialises JSON-LD so that a "<" inside any text can never close the script tag. */
const serialize = (data: unknown) => JSON.stringify(data).replace(/</g, "\\u003c");

export const JsonLd = ({ data }: { data: Record<string, unknown> }) => (
  <script type="application/ld+json">{serialize({ "@context": "https://schema.org", ...data })}</script>
);

const areaServed = BUSINESS.areaServed.map((name) =>
  name.endsWith("Kreis") ? { "@type": "AdministrativeArea", name } : { "@type": "City", name },
);

/** The one business block. It is rendered on every page from the root route, so pages never repeat it. */
export const LocalBusinessSchema = () => (
  <JsonLd
    data={{
      "@type": ["LocalBusiness", "ProfessionalService"],
      "@id": BUSINESS.id,
      name: BUSINESS.name,
      alternateName: BUSINESS.shortName,
      description: BUSINESS.description,
      url: BUSINESS.url,
      logo: BUSINESS.logo,
      image: BUSINESS.image,
      telephone: BUSINESS.telephone,
      email: BUSINESS.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS.address.street,
        addressLocality: BUSINESS.address.city,
        postalCode: BUSINESS.address.postalCode,
        addressRegion: BUSINESS.address.region,
        addressCountry: BUSINESS.address.country,
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: BUSINESS.geo.latitude,
        longitude: BUSINESS.geo.longitude,
      },
      areaServed,
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: BUSINESS.openingHours.days,
          opens: BUSINESS.openingHours.opens,
          closes: BUSINESS.openingHours.closes,
        },
      ],
      priceRange: BUSINESS.priceRange,
      founder: {
        "@type": "Person",
        "@id": BUSINESS.founderId,
        name: BUSINESS.founder,
        url: BUSINESS.founderId,
        jobTitle: BUSINESS.founderJobTitle,
      },
      foundingDate: BUSINESS.foundingDate,
      knowsLanguage: BUSINESS.knowsLanguage,
      sameAs: SAME_AS,
    }}
  />
);

interface ServiceSchemaProps {
  /** Short German service type, e.g. "Fotografie". */
  serviceType: string;
  name: string;
  /** Site path of the service page, e.g. "/foto". */
  path: string;
  description: string;
}

/** Service block for a service page; the provider is the business block by its @id. */
export const ServiceSchema = ({ serviceType, name, path, description }: ServiceSchemaProps) => (
  <JsonLd
    data={{
      "@type": "Service",
      "@id": `${SITE_URL}${path}#service`,
      name,
      serviceType,
      description,
      url: `${SITE_URL}${path}`,
      provider: { "@id": BUSINESS.id },
      areaServed,
    }}
  />
);

export const BreadcrumbSchema = ({ items }: { items: Array<{ name: string; url: string }> }) => (
  <JsonLd
    data={{
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    }}
  />
);

export const FAQSchema = ({ faqs }: { faqs: Array<{ question: string; answer: string }> }) => (
  <JsonLd
    data={{
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    }}
  />
);

interface ArticleSchemaProps {
  article: {
    title: string;
    description: string;
    url: string;
    datePublished: string;
    dateModified?: string;
    image?: string;
  };
}

/** Article block for a blog post; the author is the founder, the publisher the business. */
export const ArticleSchema = ({ article }: ArticleSchemaProps) => (
  <JsonLd
    data={{
      "@type": "Article",
      headline: article.title,
      description: article.description,
      url: article.url,
      mainEntityOfPage: article.url,
      inLanguage: "de",
      datePublished: article.datePublished,
      dateModified: article.dateModified ?? article.datePublished,
      ...(article.image ? { image: article.image } : {}),
      author: {
        "@type": "Person",
        "@id": BUSINESS.founderId,
        name: BUSINESS.founder,
        url: BUSINESS.founderId,
      },
      publisher: {
        "@type": "Organization",
        "@id": BUSINESS.id,
        name: BUSINESS.name,
        logo: { "@type": "ImageObject", url: BUSINESS.logo },
      },
    }}
  />
);
